import React, { useCallback, useEffect, useMemo, useState } from "react";
import NumberFormat from "react-number-format";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  IconButton,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { CalculateMeasure, CalculateMeasureGroup } from "assets/material-custom-icons";
import { ProgressOverlay } from "components";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useDidUpdate } from "hooks";
import { useAccountSelector } from "store";
import { CurrencyType, UiMeasureDataFiles } from "store/types";

import { currencySign } from "../../../constants";
import { AttachFiles } from "./AttachFiles";
import { GroupedMeasures } from "./GroupedMeasures";
import { IGroupsFormContext, MeasureData as IMeasureData, useGroupsForm } from "./GroupsFormContext";
import { MeasureData } from "./MeasureData";
import { SectionSelects } from "./SectionSelects";
import { TotalValueForGroup } from "./TotalValueForGroup";
import { ZeroMeasureData } from "./ZeroMeasureData";

const howManyToShow = 3;

interface Props {
  hideRoundName: boolean;
  deal_id: number;
  title?: string;
  round_id?: number | string;
  measureData: IMeasureData[];
  defaultCurrency: CurrencyType;
  isCurrencyMeasure: boolean;
  group_id: number;
  isAllowedNegative: boolean;
  disableAddMeasureData?: boolean;
  parentDealId: number | null;
  displaySecondPart?: boolean;
  groupFiles: Record<string | number, UiMeasureDataFiles> | null;
  isMeasureGlobalValue: boolean;
  total_amount: number;
}

type measureDataByInvestorGroup = {
  investor_group: string | null;
  measureData: IMeasureData[];
};

type DataToRender = Array<IMeasureData | measureDataByInvestorGroup>;

const pl = "32px";

const getMeasureTotal = (measureData: IMeasureData[]) =>
  String(measureData.reduce((acc, item) => (acc += Number(item.value)), 0) || "");

export const Section: React.FC<Props> = ({
  title,
  measureData,
  hideRoundName,
  deal_id,
  round_id,
  defaultCurrency,
  isCurrencyMeasure,
  group_id,
  isAllowedNegative,
  disableAddMeasureData,
  parentDealId,
  displaySecondPart = true,
  groupFiles,
  isMeasureGlobalValue,
  total_amount
}) => {
  const { allow_split_by_group } = useAccountSelector() || {};

  // Remove zero key element. Zero key is needed to show rounds without investors
  const measureWithoutZeroKey = useMemo(() => measureData.filter((item) => item.key), [measureData]);

  const dataToRender: DataToRender = useMemo(() => {
    if (allow_split_by_group) {
      const groupedMap = new Map<string, IMeasureData[]>();
      const ungroupedItems: IMeasureData[] = [];

      measureWithoutZeroKey.forEach((item) => {
        const group = item.investor_group;
        if (group) {
          if (!groupedMap.has(group)) {
            groupedMap.set(group, []);
          }
          groupedMap.get(group)!.push(item);
        } else {
          ungroupedItems.push(item);
        }
      });

      const result: DataToRender = [...ungroupedItems];
      groupedMap.forEach((measureData, investor_group) => {
        result.push({ investor_group, measureData });
      });

      return result;
    }
    return measureWithoutZeroKey;
  }, [measureWithoutZeroKey, allow_split_by_group]);

  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [totalValue, setTotalValue] = useState<string>(getMeasureTotal(measureWithoutZeroKey) || String(total_amount));
  const [calcLoading, setCalcLoading] = useState<boolean>(false);
  const [selectSplit, setSelectSplit] = useState<HTMLButtonElement | null>(null);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

  const { calculateMeasureData, findFirstMeasureDataError, setMeasureDataFilesPickerParam } = useGroupsForm();

  const isSingleInvestor = useMemo(() => measureWithoutZeroKey.length === 1, [measureWithoutZeroKey]);
  const isExistingRound = typeof round_id === "number";

  const selectedDateAndCurrencies = useMemo<
    Pick<IMeasureData, "currency" | "date" | "accounting_date" | "effective_range_from" | "effective_range_to">
  >(() => {
    const result: Pick<
      IMeasureData,
      "currency" | "date" | "accounting_date" | "effective_range_from" | "effective_range_to"
    > = {
      currency: null,
      date: null,
      accounting_date: null,
      effective_range_from: null,
      effective_range_to: null
    };

    if (measureWithoutZeroKey.length) {
      result.currency = measureWithoutZeroKey[0].currency;
      result.date = measureWithoutZeroKey[0].date;
      result.accounting_date = measureWithoutZeroKey[0].accounting_date;
      result.effective_range_from = measureWithoutZeroKey[0].effective_range_from;
      result.effective_range_to = measureWithoutZeroKey[0].effective_range_to;
    }

    return result;
  }, [measureWithoutZeroKey]);

  const currencySymbol = useMemo<string | undefined>(() => {
    const { currency } = selectedDateAndCurrencies;
    if (!currency) return;
    return currencySign[currency];
  }, [selectedDateAndCurrencies]);

  const measureDataKeys = useMemo<number[]>(() => {
    return measureWithoutZeroKey.map((item) => item.key);
  }, [measureWithoutZeroKey]);

  const sectionFirstError = findFirstMeasureDataError(measureDataKeys);

  const splitElements = useMemo(() => {
    if (dataToRender.length <= howManyToShow) {
      return {
        start: dataToRender
      };
    }
    return {
      start: dataToRender.slice(0, howManyToShow),
      rest: dataToRender.slice(howManyToShow)
    };
  }, [dataToRender]);

  const [zeroElementData, setZeroElementData] = useState<{
    //we need this state in case to keep values (mostly dates) that user selected before investor selection initiate measure data creation
    currency: CurrencyType | null;
    date: string | null;
    accounting_date: string | null;
    effective_range_from: string | null;
    effective_range_to: string | null;
  }>({
    currency: null,
    date: null,
    accounting_date: null,
    effective_range_from: null,
    effective_range_to: null
  });

  const asyncCalc = useCallback<IGroupsFormContext["calculateMeasureData"]>(
    async (data) => {
      try {
        setCalcLoading(true);
        await calculateMeasureData(data);
      } catch (e) {
        throw e;
      } finally {
        setCalcLoading(false);
      }
    },
    [calculateMeasureData]
  );

  useDidUpdate(() => {
    const measureDataTotal = getMeasureTotal(measureWithoutZeroKey);
    if (measureDataTotal === totalValue) return;
    setTotalValue(measureDataTotal);
  }, [measureWithoutZeroKey]);

  const groupFilesForTotal = useMemo(() => {
    if (!round_id || !groupFiles) return null;
    return groupFiles[round_id] || null;
  }, [round_id, groupFiles]);

  useEffect(() => {
    if (selectedGroups.length) {
      setSelectedGroups([]);
    }
  }, [measureData]);

  return (
    <ProgressOverlay inProgress={calcLoading} disableOverlayBgc>
      <Stack sx={{ gap: "10px", pt: "20px" }}>
        {!hideRoundName && (
          <Typography sx={{ color: "#0A3142", fontSize: "16px", fontWeight: 600, pl: pl }}>{title}</Typography>
        )}
        <SectionSelects
          firstFoundError={sectionFirstError}
          {...selectedDateAndCurrencies}
          measureDataKeys={measureDataKeys}
          defaultCurrency={defaultCurrency}
          isCurrencyMeasure={isCurrencyMeasure}
          setZeroElementData={setZeroElementData}
          zeroDataFrom={zeroElementData.effective_range_from}
          zeroDataTo={zeroElementData.effective_range_to}
        />
        {splitElements.start.map((item, idx) => {
          // Type guard: check if item is measureDataByInvestorGroup
          if ("measureData" in item) {
            // Has investor_group - render as GroupedMeasures
            if (item.investor_group && item.measureData.length) {
              return (
                <GroupedMeasures
                  group_id={group_id}
                  investorGroup={item.investor_group}
                  key={idx}
                  groupMeasureData={item.measureData}
                  valuePrefix={currencySymbol}
                  deal_id={deal_id}
                  calculateMeasureData={asyncCalc}
                  isAllowedNegative={isAllowedNegative}
                  parentDealId={parentDealId}
                  isMeasureGlobalValue={isMeasureGlobalValue}
                  setSelectedGroups={setSelectedGroups}
                  selectedGroups={selectedGroups}
                />
              );
            }
            return null;
          }
          // Regular IMeasureData without group
          return (
            <MeasureData
              key={item.key}
              measureData={item}
              hideCalculateButton={isSingleInvestor}
              valuePrefix={currencySymbol}
              deal_id={deal_id}
              calculateMeasureData={asyncCalc}
              isAllowedNegative={isAllowedNegative}
              parentDealId={parentDealId}
              isMeasureGlobalValue={isMeasureGlobalValue}
            />
          );
        })}

        {displaySecondPart && (
          <Accordion
            expanded={isExpanded}
            disableGutters
            square
            sx={{
              display: "flex",
              flexDirection: "column-reverse",
              "&.MuiAccordion-root:before": {
                backgroundColor: "transparent"
              },
              bgcolor: "transparent",
              border: "none",
              boxShadow: "none"
            }}
          >
            <AccordionSummary
              sx={{
                pl: 0,
                pr: "40px",
                cursor: "default",
                ".MuiAccordionSummary-content": {
                  m: 0
                },
                ".MuiIconButton-root": {
                  ...(!selectSplit && { opacity: 0 }),
                  transition: "all 0.2s linear"
                },
                ":hover": {
                  ".MuiIconButton-root": {
                    opacity: 1
                  }
                }
              }}
            >
              <Stack sx={{ gap: "10px", width: "100%" }}>
                {!disableAddMeasureData && round_id && (
                  <Stack sx={{ pl: "32px" }}>
                    <ZeroMeasureData
                      group_id={group_id}
                      round_id={round_id}
                      valuePrefix={currencySymbol}
                      isAllowedNegative={isAllowedNegative}
                      {...zeroElementData}
                    />
                  </Stack>
                )}
                {Boolean(selectedGroups.length) && round_id && (
                  <TotalValueForGroup
                    group_id={group_id}
                    currencySymbol={currencySymbol}
                    isAllowedNegative={isAllowedNegative}
                    roundId={Number(round_id)}
                    // get selected investors ids for the measure data by selected groups
                    selectedInvestorsIds={measureData
                      .filter((item) => item.investor_group && selectedGroups.includes(item.investor_group))
                      .map((item) => item.investor_id as number)}
                  />
                )}

                <Stack direction="row" sx={{ gap: "10px", alignItems: "center" }}>
                  <Stack justifyContent="center" sx={{ minWidth: allow_split_by_group ? 230 : 223, cursor: "default" }}>
                    {splitElements.rest && (
                      <Button
                        sx={{
                          bgcolor: "#EDF1F9",
                          boxShadow: "none",
                          color: "#0A3142",
                          alignSelf: "flex-start"
                        }}
                        size="sm"
                        variant="contained"
                        startIcon={
                          <ArrowDropDownRoundedIcon {...(isExpanded && { sx: { transform: "rotate(180deg)" } })} />
                        }
                        onClick={() => setIsExpanded((prevValue) => !prevValue)}
                      >
                        Show all investors
                      </Button>
                    )}
                  </Stack>
                  {round_id && measureWithoutZeroKey.length > 1 && (
                    <>
                      <NumberFormat
                        thousandSeparator
                        isNumericString
                        variant="outlined"
                        prefix={currencySymbol}
                        decimalScale={2}
                        label="Value"
                        placeholder="Add value"
                        sx={{
                          maxWidth: "160px",
                          ".MuiInputBase-root": {
                            pr: 0,
                            overflow: "hidden"
                          },
                          ".MuiInputBase-input": {
                            padding: "9.5px 8px"
                          },
                          ".MuiButtonBase-root": {
                            opacity: selectSplit ? 1 : 0,
                            transition: "opacity 0.2s"
                          },
                          "&:hover": {
                            ".MuiButtonBase-root": {
                              opacity: 1
                            }
                          }
                        }}
                        allowNegative={isAllowedNegative}
                        slotProps={{
                          input: {
                            ...(isExistingRound && {
                              endAdornment: (
                                <MaterialTooltip isShow title="Split the value by commitments">
                                  <IconButton
                                    disabled={!totalValue}
                                    onClick={(e) => {
                                      if (parentDealId) {
                                        return setSelectSplit(e.currentTarget);
                                      }
                                      asyncCalc({
                                        round_id: round_id,
                                        value: totalValue,
                                        split_type: "by_commitments",
                                        // split_type: "by_parent_commitments",
                                        ...(parentDealId && { parent_deal_id: parentDealId }),
                                        group_id
                                      });
                                    }}
                                    sx={{
                                      "&.Mui-disabled": {
                                        background: "#eaeaea",
                                        color: "#c0c0c0"
                                      },
                                      height: "42px",
                                      width: "50px",
                                      bgcolor: "#0A3142",
                                      borderRadius: 0,

                                      ":hover": {
                                        bgcolor: "#0A3142"
                                      }
                                    }}
                                  >
                                    <CalculateMeasure />

                                    {parentDealId && (
                                      <ArrowDropDownIcon
                                        sx={{
                                          fontSize: "18px",
                                          color: "#fff",
                                          transform: selectSplit ? "rotate(180deg)" : "rotate(0deg)",
                                          transition: "transform 0.2s ease-in-out"
                                        }}
                                      />
                                    )}
                                  </IconButton>
                                </MaterialTooltip>
                              )
                            })
                          }
                        }}
                        customInput={TextField}
                        value={totalValue}
                        onValueChange={({ value }) => setTotalValue(value)}
                      />
                      {/* show files count for group (Total) */}
                      <AttachFiles
                        isTotal={true}
                        isMeasureGlobalValue={isMeasureGlobalValue}
                        files={groupFilesForTotal}
                        setMeasureDataFilesPickerParam={setMeasureDataFilesPickerParam}
                        group_id={group_id}
                        measureDataKey={null}
                        roundId={round_id || null}
                      />
                    </>
                  )}
                </Stack>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0, pb: "10px" }}>
              <Stack gap="10px" alignItems="stretch">
                {splitElements.rest?.map((item, idx) => {
                  // Type guard: check if item is measureDataByInvestorGroup
                  if ("measureData" in item) {
                    // Has investor_group - render as GroupedMeasures
                    if (item.investor_group && item.measureData.length) {
                      return (
                        <GroupedMeasures
                          group_id={group_id}
                          investorGroup={item.investor_group}
                          key={idx}
                          groupMeasureData={item.measureData}
                          valuePrefix={currencySymbol}
                          deal_id={deal_id}
                          calculateMeasureData={asyncCalc}
                          isAllowedNegative={isAllowedNegative}
                          parentDealId={parentDealId}
                          isMeasureGlobalValue={isMeasureGlobalValue}
                          setSelectedGroups={setSelectedGroups}
                          selectedGroups={selectedGroups}
                        />
                      );
                    }
                    return null;
                  }
                  // Regular IMeasureData without group
                  return (
                    <MeasureData
                      key={item.key}
                      measureData={item}
                      hideCalculateButton={isSingleInvestor}
                      valuePrefix={currencySymbol}
                      deal_id={deal_id}
                      calculateMeasureData={asyncCalc}
                      isAllowedNegative={isAllowedNegative}
                      parentDealId={parentDealId}
                      isMeasureGlobalValue={isMeasureGlobalValue}
                    />
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}
      </Stack>
      <Popover
        open={Boolean(selectSplit)}
        anchorEl={selectSplit}
        onClose={() => setSelectSplit(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
              mt: "4px"
            }
          }
        }}
      >
        <Stack sx={{ py: "8px" }}>
          <MenuItem
            onClick={() => {
              setSelectSplit(null);
              if (!round_id) return;
              if (!isExistingRound) return;
              asyncCalc({
                round_id,
                value: totalValue,
                split_type: "by_commitments",
                group_id
              });
            }}
            sx={{ gap: "12px", py: "10px", px: "16px" }}
          >
            <CalculateMeasure sx={{ "& path": { fill: "#1C1B1F" } }} />
            <Typography sx={{ fontSize: "14px", color: "#0A3142" }}>Split by commitments</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setSelectSplit(null);
              if (!round_id) return;
              if (!isExistingRound) return;
              asyncCalc({
                round_id,
                value: totalValue,
                split_type: "by_parent_commitments",
                ...(parentDealId && { parent_deal_id: parentDealId }),
                group_id
              });
            }}
            sx={{ gap: "12px", py: "10px", px: "16px" }}
          >
            <CalculateMeasureGroup />
            <Typography sx={{ fontSize: "14px", color: "#0A3142" }}>Split by parent commitments</Typography>
          </MenuItem>
        </Stack>
      </Popover>
    </ProgressOverlay>
  );
};
